/**
 * Custom error types for Address Search Field
 * Provides better error handling and debugging capabilities
 */

/**
 * Base class for all address search errors
 */
export class AddressSearchError extends Error {
  readonly code: string;
  readonly originalError?: Error;

  constructor(message: string, code: string, originalError?: Error) {
    super(message);
    this.name = "AddressSearchError";
    this.code = code;
    this.originalError = originalError;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AddressSearchError);
    }
  }
}

/**
 * Error thrown when API request times out
 */
export class AddressSearchTimeoutError extends AddressSearchError {
  constructor(timeoutMs: number, originalError?: Error) {
    super(
      `Request timeout - address search took longer than ${timeoutMs}ms`,
      "TIMEOUT",
      originalError,
    );
    this.name = "AddressSearchTimeoutError";
  }
}

/**
 * Error thrown when API request fails
 */
export class AddressSearchApiError extends AddressSearchError {
  readonly status?: number;
  readonly statusText?: string;

  constructor(status?: number, statusText?: string, originalError?: Error) {
    const message = status
      ? `API request failed with status ${status}: ${statusText || "Unknown error"}`
      : "API request failed";
    super(message, "API_FAILURE", originalError);
    this.name = "AddressSearchApiError";
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * Error thrown when network connectivity issues occur
 */
export class AddressSearchNetworkError extends AddressSearchError {
  constructor(originalError?: Error) {
    super(
      "Network error occurred while searching for addresses",
      "NETWORK_ERROR",
      originalError,
    );
    this.name = "AddressSearchNetworkError";
  }
}

/**
 * Error thrown when place prediction is not found
 */
export class AddressSearchPredictionError extends AddressSearchError {
  constructor(placeId: string, originalError?: Error) {
    super(
      `Prediction not found for place ID: ${placeId}`,
      "PREDICTION_NOT_FOUND",
      originalError,
    );
    this.name = "AddressSearchPredictionError";
  }
}

/**
 * Error thrown when input validation fails
 */
export class AddressSearchValidationError extends AddressSearchError {
  constructor(message: string, originalError?: Error) {
    super(message, "VALIDATION_ERROR", originalError);
    this.name = "AddressSearchValidationError";
  }
}

/**
 * Type guard to check if error is an AbortError
 */
export function isAbortError(error: unknown): error is Error {
  return error instanceof Error && error.name === "AbortError";
}

/**
 * Type guard to check if error is an AddressSearchError
 */
export function isAddressSearchError(
  error: unknown,
): error is AddressSearchError {
  return error instanceof AddressSearchError;
}

/**
 * Determines user-friendly error message based on error type
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  if (isAbortError(error)) {
    return "Search was cancelled";
  }

  if (error instanceof AddressSearchTimeoutError) {
    return "The search is taking longer than expected. Please try again or enter your address manually.";
  }

  if (error instanceof AddressSearchApiError) {
    if (error.status === 429) {
      return "Too many requests. Please wait a moment and try again.";
    }
    if (error.status === 403) {
      return "Address search is currently unavailable. Please enter your address manually.";
    }
    return "Unable to search addresses at this time. Please try again or enter your address manually.";
  }

  if (error instanceof AddressSearchNetworkError) {
    return "Network connection issue. Please check your connection and try again.";
  }

  if (error instanceof AddressSearchPredictionError) {
    return "Unable to load selected address. Please try selecting a different address.";
  }

  if (error instanceof AddressSearchValidationError) {
    return error.message;
  }

  // Generic fallback
  return "Unable to search addresses. Please try again later or enter your address manually.";
}

/**
 * Logs error with appropriate level and context
 */
export function logAddressSearchError(
  error: unknown,
  context: string,
  additionalData?: Record<string, unknown>,
): void {
  // Skip logging for abort errors (expected behavior)
  if (isAbortError(error)) {
    return;
  }

  const errorData = {
    context,
    error:
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error,
    ...additionalData,
  };

  // For timeout errors, log as warning (might be temporary network issue)
  if (error instanceof AddressSearchTimeoutError) {
    console.warn("Address search timeout:", errorData);
    return;
  }

  // For network errors, log as warning
  if (error instanceof AddressSearchNetworkError) {
    console.warn("Address search network error:", errorData);
    return;
  }

  // For all other errors, log as error
  console.error("Address search error:", errorData);
}
